import React, { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showError, showSuccessAlert } from '../../../../core/alert/sweetAlertConfig';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';

interface CreateFloorModuleProps {
  show: boolean;
  onHide: () => void;
  buildingId: number;
  buildingName: string;
  onSuccess: () => void;
}

interface FloorFormData {
  name: string;
  description: string;
  floorNumber: number;
  buildingId: number;
}

const CreateFloorModule: React.FC<CreateFloorModuleProps> = ({
  show,
  onHide,
  buildingId,
  buildingName,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<FloorFormData>({
    name: '',
    description: '',
    floorNumber: 1,
    buildingId,
  });
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'floorNumber' ? parseInt(value, 10) || 1 : value,
    }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!checkPermission('ROLE_CREATE_FLOOR')) {
      showError('You do not have permission to create floors.');
      return;
    }
    if (!formData.name.trim()) {
      setError('Floor name is required.');
      return;
    }
    if (formData.floorNumber < 1) {
      setError('Floor number must be at least 1.');
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await apiDefinitions.createFloor(formData);
      if (response.status === 200) {
        showSuccessAlert("Floor created successfully");
        onSuccess();
        onHide();
        setFormData({ name: '', description: '', floorNumber: 1, buildingId });
      } else {
        showError(response.data?.statusDescription || 'Failed to create floor.');
      }
    } catch (error: any) {
      showError(error.response?.data?.statusDescription || 'Failed to create floor. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Create Floor for {buildingName}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label>Floor Name</Form.Label>
            <Form.Control
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder="Enter floor name"
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Floor Number</Form.Label>
            <Form.Control
              type="number"
              name="floorNumber"
              value={formData.floorNumber}
              onChange={handleInputChange}
              placeholder="Enter floor number"
              min="1"
              required
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder="Enter description (optional)"
            />
          </Form.Group>
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}
          <div className="d-flex justify-content-end gap-2">
            <Button variant="primary" onClick={onHide} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" variant="success" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Floor'}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default CreateFloorModule;